// 화면 17 — 섹터 스코어 재산정 모달.
// 권위: 사용자 결정 2026-05-15.
// pca-modal 디자인 시스템 (pendingCa.css 글로벌 활용).
//
// lookback 옵션: 30 / 60 (default) / 90 / custom (5..252).
// 예상 소요: lookbackDays * 2 분 (단순 계수 — 실제 ETA 는 polling tick 에서 표시).
// running 상태에서 모달을 다시 열면 현재 진행률을 표시 (실행 버튼 비활성).

import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { SectorRecomputeJob } from '../../api/sectorRecomputeApi';
import type { RecomputePhase } from './useSectorRecompute';

interface Props {
  phase: RecomputePhase;
  activeJob: SectorRecomputeJob | null;
  onSubmit: (
    lookbackDays: number,
    fullReset: boolean,
    recomputeLeaderStocks: boolean
  ) => Promise<void>;
  onClose: () => void;
}

type Preset = '30' | '60' | '90' | 'custom';

const PRESETS: { value: Exclude<Preset, 'custom'>; days: number }[] = [
  { value: '30', days: 30 },
  { value: '60', days: 60 },
  { value: '90', days: 90 },
];

const CUSTOM_MIN = 5;
const CUSTOM_MAX = 252;

export function SectorRecomputeModal({
  phase,
  activeJob,
  onSubmit,
  onClose,
}: Props): JSX.Element {
  const [preset, setPreset] = useState<Preset>('60');
  const [customDays, setCustomDays] = useState<string>('60');
  const [fullReset, setFullReset] = useState<boolean>(false);
  const [recomputeLeaderStocks, setRecomputeLeaderStocks] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const isBusy = phase === 'submitting' || phase === 'running';

  // 선택된 lookbackDays — null 이면 invalid.
  // fullReset=true 인 경우 lookback 입력은 무시 (서버가 전체 거래일 분포 사용)되지만
  // request body 호환성을 위해 현재 선택값(또는 기본 60)을 그대로 전달한다.
  const selectedDays: number | null = useMemo(() => {
    if (preset !== 'custom') {
      return PRESETS.find((p) => p.value === preset)?.days ?? null;
    }
    const n = Number.parseInt(customDays, 10);
    if (!Number.isFinite(n)) return null;
    if (n < CUSTOM_MIN || n > CUSTOM_MAX) return null;
    return n;
  }, [preset, customDays]);

  // ETA 계수 — lookbackDays * 2 분 (사용자 안내 텍스트).
  const estimatedMinutes = selectedDays !== null ? selectedDays * 2 : null;

  // Esc 닫기.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && phase !== 'submitting') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, phase]);

  async function handleSubmit() {
    if (isBusy) return;
    // fullReset 모드면 lookback 입력은 의미가 없으므로 검증 스킵 (기본 60 전송).
    const daysToSend = fullReset ? (selectedDays ?? 60) : selectedDays;
    if (!fullReset && selectedDays === null) {
      setValidationError(`custom 일수는 ${CUSTOM_MIN}..${CUSTOM_MAX} 범위여야 합니다.`);
      return;
    }
    setValidationError(null);
    await onSubmit(daysToSend as number, fullReset, recomputeLeaderStocks);
  }

  return (
    <div className="pca-modal__backdrop" role="dialog" aria-modal="true" aria-labelledby="sr-modal-title">
      <div className="pca-modal" data-testid="sector-recompute-modal" style={{ width: 'min(520px, 100%)' }}>
        <div className="pca-modal__header">
          <h2 className="pca-modal__title" id="sr-modal-title">섹터 스코어 재산정</h2>
          <button
            type="button"
            className="pca-modal__close"
            onClick={onClose}
            disabled={phase === 'submitting'}
            aria-label="닫기"
          >
            ×
          </button>
        </div>

        <div className="pca-modal__body">
          {phase === 'running' && activeJob ? (
            <RunningView job={activeJob} />
          ) : (
            <SubmitForm
              preset={preset}
              setPreset={setPreset}
              customDays={customDays}
              setCustomDays={setCustomDays}
              fullReset={fullReset}
              setFullReset={setFullReset}
              recomputeLeaderStocks={recomputeLeaderStocks}
              setRecomputeLeaderStocks={setRecomputeLeaderStocks}
              estimatedMinutes={estimatedMinutes}
              validationError={validationError}
              disabled={isBusy}
            />
          )}
        </div>

        <div className="pca-modal__actions">
          <button
            type="button"
            className="pca-btn pca-btn--ghost"
            onClick={onClose}
            disabled={phase === 'submitting'}
            data-testid="sector-recompute-cancel"
          >
            닫기
          </button>
          {phase !== 'running' && (
            <button
              type="button"
              className="pca-btn pca-btn--primary"
              onClick={handleSubmit}
              disabled={isBusy || (!fullReset && selectedDays === null)}
              data-testid="sector-recompute-submit"
            >
              {phase === 'submitting' ? (
                <>
                  <Loader2 size={14} className="pca-spin" /> 요청 중…
                </>
              ) : (
                '재산정 실행'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface FormProps {
  preset: Preset;
  setPreset: (p: Preset) => void;
  customDays: string;
  setCustomDays: (s: string) => void;
  fullReset: boolean;
  setFullReset: (b: boolean) => void;
  recomputeLeaderStocks: boolean;
  setRecomputeLeaderStocks: (b: boolean) => void;
  estimatedMinutes: number | null;
  validationError: string | null;
  disabled: boolean;
}

function SubmitForm({
  preset,
  setPreset,
  customDays,
  setCustomDays,
  fullReset,
  setFullReset,
  recomputeLeaderStocks,
  setRecomputeLeaderStocks,
  estimatedMinutes,
  validationError,
  disabled,
}: FormProps): JSX.Element {
  // fullReset 체크 시 lookback 입력은 의미가 없어 비활성화.
  const lookbackDisabled = disabled || fullReset;
  return (
    <>
      <div className="pca-section">
        <h3 className="pca-section__title">Lookback 일수</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {PRESETS.map((p) => (
            <label key={p.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: 'var(--text-sm)', opacity: fullReset ? 0.5 : 1 }}>
              <input
                type="radio"
                name="sr-lookback"
                value={p.value}
                checked={preset === p.value}
                onChange={() => setPreset(p.value)}
                disabled={lookbackDisabled}
                data-testid={`sector-recompute-preset-${p.value}`}
              />
              {p.days}일
            </label>
          ))}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: 'var(--text-sm)', opacity: fullReset ? 0.5 : 1 }}>
            <input
              type="radio"
              name="sr-lookback"
              value="custom"
              checked={preset === 'custom'}
              onChange={() => setPreset('custom')}
              disabled={lookbackDisabled}
              data-testid="sector-recompute-preset-custom"
            />
            custom
            <input
              type="number"
              min={CUSTOM_MIN}
              max={CUSTOM_MAX}
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
              disabled={lookbackDisabled || preset !== 'custom'}
              style={{
                width: '80px',
                padding: '3px 6px',
                border: '1px solid var(--seal-line-bold)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-sm)',
                background: 'var(--inset)',
                color: 'var(--ink)',
              }}
              data-testid="sector-recompute-custom-input"
              aria-label="custom 일수"
            />
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-faint)' }}>
              ({CUSTOM_MIN}~{CUSTOM_MAX})
            </span>
          </label>
          {fullReset && (
            <p
              data-testid="sector-recompute-fullreset-note"
              style={{ margin: '4px 0 0', fontSize: 'var(--text-xs)', color: 'var(--ink-quiet)' }}
            >
              전체 거래일 재산정 모드 — lookback 입력은 무시됩니다.
            </p>
          )}
        </div>
      </div>

      <div className="pca-section">
        <h3 className="pca-section__title">운영 옵션</h3>
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            fontSize: 'var(--text-sm)',
            color: 'var(--ink)',
          }}
          title="daily_sector_history 전체 DELETE 후 stock_daily_score 거래일 분포 기준 재산정. sector 매핑 변경 후 사용 권장."
        >
          <input
            type="checkbox"
            checked={fullReset}
            onChange={(e) => setFullReset(e.target.checked)}
            disabled={disabled}
            data-testid="sector-recompute-full-reset"
            aria-describedby="sr-fullreset-desc"
            style={{ marginTop: '3px' }}
          />
          <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span>전체 reset (과거 잔재 정리)</span>
            <span
              id="sr-fullreset-desc"
              style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-faint)' }}
            >
              daily_sector_history 전체 DELETE 후 stock_daily_score 거래일 분포 기준 재산정.
              sector 매핑 변경(자식 비활성화 등) 후 사용 권장.
            </span>
          </span>
        </label>

        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            fontSize: 'var(--text-sm)',
            color: 'var(--ink)',
            marginTop: '10px',
          }}
          title="섹터 머지 후 leader_stocks 가 새 매핑 기준으로 재산출되도록 함. 소요 시간 5~10분 증가."
        >
          <input
            type="checkbox"
            checked={recomputeLeaderStocks}
            onChange={(e) => setRecomputeLeaderStocks(e.target.checked)}
            disabled={disabled}
            data-testid="sector-recompute-leader-stocks"
            aria-describedby="sr-leaderstocks-desc"
            style={{ marginTop: '3px' }}
          />
          <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span>주도주 (leader_stocks_daily) 도 함께 재산출</span>
            <span
              id="sr-leaderstocks-desc"
              style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-faint)' }}
            >
              섹터 머지 후 leader_stocks 가 새 매핑 기준으로 재산출되도록 함.
              소요 시간 5~10분 증가.
            </span>
          </span>
        </label>
      </div>

      <div className="pca-section">
        <h3 className="pca-section__title">예상 소요 시간</h3>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-quiet)', margin: 0 }}>
          {fullReset
            ? '전체 거래일 재산정 — 데이터량에 따라 수십 분~수 시간 소요'
            : estimatedMinutes !== null
              ? `약 ${estimatedMinutes}분 (lookback × 2분 기준 — 실제 시간은 데이터량에 따라 변동)`
              : '유효한 일수를 선택하세요.'}
        </p>
        {recomputeLeaderStocks && (
          <p
            data-testid="sector-recompute-leader-stocks-note"
            style={{ margin: '4px 0 0', fontSize: 'var(--text-xs)', color: 'var(--ink-quiet)' }}
          >
            주도주 재산출 포함 — 예상 소요 시간 +5~10분.
          </p>
        )}
      </div>

      {validationError && <div className="pca-warning-banner">{validationError}</div>}
    </>
  );
}

function RunningView({ job }: { job: SectorRecomputeJob }): JSX.Element {
  const pct = job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;
  const etaText = formatEta(job.etaMs);
  return (
    <>
      <div className="pca-section">
        <h3 className="pca-section__title">재산정 진행 중</h3>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-quiet)', margin: 0 }}>
          job_id <code style={{ fontFamily: 'var(--font-mono)' }}>{job.jobId}</code>
          {job.triggeredBy ? ` · trigger ${job.triggeredBy}` : ''}
        </p>
      </div>
      <div className="pca-section">
        <h3 className="pca-section__title">진행률</h3>
        <div data-testid="sector-recompute-progress" style={{ fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)' }}>
          {job.processed} / {job.total} ({pct}%){job.currentDate ? ` · ${job.currentDate}` : ''}
        </div>
        <div
          style={{
            background: 'var(--inset)',
            border: '1px solid var(--seal-line)',
            borderRadius: 'var(--radius-sm)',
            height: '10px',
            overflow: 'hidden',
            marginTop: '6px',
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              background: 'var(--accent)',
              transition: 'width 200ms ease',
            }}
          />
        </div>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--ink-faint)', margin: '6px 0 0' }}>
          ETA {etaText} · lookback {job.lookbackDays}일 ({job.startDate} ~ {job.endDate})
        </p>
      </div>
    </>
  );
}

function formatEta(etaMs: number | null): string {
  if (etaMs === null || etaMs < 0) return '계산 중';
  const totalSec = Math.round(etaMs / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m === 0) return `${s}초`;
  return `${m}분 ${s.toString().padStart(2, '0')}초`;
}
